/**
 * Smooth Camera Controller
 * Handles camera transitions and user input
 */

import * as THREE from 'three'
import TWEEN from '@tweenjs/tween.js'
import gsap from 'gsap'

export class CameraController {
  constructor(camera, domElement, options = {}) {
    this.camera = camera
    this.domElement = domElement
    
    this.options = {
      minDistance: options.minDistance || 5,
      maxDistance: options.maxDistance || 25,
      panSpeed: options.panSpeed || 0.5,
      zoomSpeed: options.zoomSpeed || 1.0,
      rotateSpeed: options.rotateSpeed || 0.5,
      dampingFactor: options.dampingFactor || 0.05,
      enableDamping: options.enableDamping !== false
    }

    this.target = new THREE.Vector3(0, 0, 0)
    this.position = camera.position.clone()
    
    this.spherical = new THREE.Spherical()
    this.sphericalDelta = new THREE.Spherical()
    
    this.isDragging = false
    this.isPanning = false
    this.lastMousePosition = { x: 0, y: 0 }
    
    this.focusedNode = null
    this.isTransitioning = false

    this.initEventListeners()
    this.updateSpherical()
  }

  initEventListeners() {
    this.domElement.addEventListener('mousedown', this.onMouseDown.bind(this))
    this.domElement.addEventListener('mousemove', this.onMouseMove.bind(this))
    this.domElement.addEventListener('mouseup', this.onMouseUp.bind(this))
    this.domElement.addEventListener('wheel', this.onWheel.bind(this))
    
    // Touch support
    this.domElement.addEventListener('touchstart', this.onTouchStart.bind(this))
    this.domElement.addEventListener('touchmove', this.onTouchMove.bind(this))
    this.domElement.addEventListener('touchend', this.onTouchEnd.bind(this))
    
    // Prevent context menu
    this.domElement.addEventListener('contextmenu', e => e.preventDefault())
  }

  updateSpherical() {
    const offset = new THREE.Vector3()
      .subVectors(this.camera.position, this.target)
    
    this.spherical.setFromVector3(offset)
  }

  onMouseDown(event) {
    if (event.button === 0) {
      this.isDragging = true
    } else if (event.button === 2) {
      this.isPanning = true
    }
    
    this.lastMousePosition.x = event.clientX
    this.lastMousePosition.y = event.clientY
  }

  onMouseMove(event) {
    if (!this.isDragging && !this.isPanning) return
    if (this.isTransitioning) return

    const deltaX = event.clientX - this.lastMousePosition.x
    const deltaY = event.clientY - this.lastMousePosition.y

    if (this.isDragging) {
      this.sphericalDelta.theta -= deltaX * this.options.rotateSpeed * 0.01
      this.sphericalDelta.phi -= deltaY * this.options.rotateSpeed * 0.01
    } else if (this.isPanning) {
      this.pan(deltaX, deltaY)
    }

    this.lastMousePosition.x = event.clientX
    this.lastMousePosition.y = event.clientY
  }

  onMouseUp() {
    this.isDragging = false
    this.isPanning = false
  }

  onWheel(event) {
    if (this.isTransitioning) return
    
    event.preventDefault()
    
    const delta = event.deltaY > 0 ? 1.1 : 0.9
    this.spherical.radius *= delta
    this.spherical.radius = THREE.MathUtils.clamp(
      this.spherical.radius,
      this.options.minDistance,
      this.options.maxDistance
    )
  }

  onTouchStart(event) {
    if (event.touches.length === 1) {
      this.isDragging = true
      this.lastMousePosition.x = event.touches[0].clientX
      this.lastMousePosition.y = event.touches[0].clientY
    }
  }

  onTouchMove(event) {
    if (!this.isDragging) return
    if (this.isTransitioning) return

    const deltaX = event.touches[0].clientX - this.lastMousePosition.x
    const deltaY = event.touches[0].clientY - this.lastMousePosition.y

    this.sphericalDelta.theta -= deltaX * this.options.rotateSpeed * 0.01
    this.sphericalDelta.phi -= deltaY * this.options.rotateSpeed * 0.01

    this.lastMousePosition.x = event.touches[0].clientX
    this.lastMousePosition.y = event.touches[0].clientY
  }

  onTouchEnd() {
    this.isDragging = false
  }

  pan(deltaX, deltaY) {
    const offset = new THREE.Vector3()
    
    // Camera left/right
    offset.setFromMatrixColumn(this.camera.matrix, 0)
    offset.multiplyScalar(-deltaX * this.options.panSpeed * 0.01)
    this.target.add(offset)

    // Camera up/down
    offset.setFromMatrixColumn(this.camera.matrix, 1)
    offset.multiplyScalar(deltaY * this.options.panSpeed * 0.01)
    this.target.add(offset)
  }

  focusOnNode(node, duration = 1.2) {
    if (this.isTransitioning) return

    this.isTransitioning = true
    this.focusedNode = node

    const nodePosition = new THREE.Vector3(
      node.position.x,
      node.position.y,
      node.position.z
    )

    const targetDistance = 6
    const offset = new THREE.Vector3(0, 1, targetDistance)
    const targetPosition = nodePosition.clone().add(offset)

    gsap.to(this.target, {
      x: nodePosition.x,
      y: nodePosition.y,
      z: nodePosition.z,
      duration: duration,
      ease: 'power3.inOut'
    })

    gsap.to(this.camera.position, {
      x: targetPosition.x,
      y: targetPosition.y,
      z: targetPosition.z,
      duration: duration,
      ease: 'power3.inOut',
      onComplete: () => {
        this.isTransitioning = false
        this.updateSpherical()
      }
    })
  }

  resetView(duration = 1.0) {
    if (this.isTransitioning) return

    this.isTransitioning = true
    this.focusedNode = null

    gsap.to(this.target, {
      x: 0,
      y: 0,
      z: 0,
      duration: duration,
      ease: 'power2.inOut'
    })

    gsap.to(this.camera.position, {
      x: 0,
      y: 3,
      z: 12,
      duration: duration,
      ease: 'power2.inOut',
      onComplete: () => {
        this.isTransitioning = false
        this.updateSpherical()
      }
    })
  }

  update() {
    if (this.isTransitioning) {
      this.camera.lookAt(this.target)
      return
    }

    // Apply damping
    if (this.options.enableDamping) {
      this.spherical.theta += this.sphericalDelta.theta * this.options.dampingFactor
      this.spherical.phi += this.sphericalDelta.phi * this.options.dampingFactor
      
      this.sphericalDelta.theta *= (1 - this.options.dampingFactor)
      this.sphericalDelta.phi *= (1 - this.options.dampingFactor)
    } else {
      this.spherical.theta += this.sphericalDelta.theta
      this.spherical.phi += this.sphericalDelta.phi
      
      this.sphericalDelta.set(0, 0, 0)
    }

    // Clamp phi
    this.spherical.phi = Math.max(
      0.1,
      Math.min(Math.PI - 0.1, this.spherical.phi)
    )

    // Update camera position
    const offset = new THREE.Vector3().setFromSpherical(this.spherical)
    this.camera.position.copy(this.target).add(offset)
    this.camera.lookAt(this.target)
  }

  dispose() {
    this.domElement.removeEventListener('mousedown', this.onMouseDown)
    this.domElement.removeEventListener('mousemove', this.onMouseMove)
    this.domElement.removeEventListener('mouseup', this.onMouseUp)
    this.domElement.removeEventListener('wheel', this.onWheel)
    this.domElement.removeEventListener('touchstart', this.onTouchStart)
    this.domElement.removeEventListener('touchmove', this.onTouchMove)
    this.domElement.removeEventListener('touchend', this.onTouchEnd)
  }
}
